"use client";

import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@devhop/ui";
import { Link, useParams, useLocation } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import type { MerchantPermissionId } from "@/modules/roles/domain/contracts/merchant-permissions";
import { hasMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { canUsePermissionWithLevel } from "@/modules/merchant/presentation/lib/shop-verification";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { useMarketplacePendingOrdersBadge } from "@/modules/merchant/presentation/lib/use-marketplace-pending-orders-badge";
import {
  getVisibleMerchantNavGroups,
  isMerchantCollapsibleActive,
  isMerchantNavLinkActive,
  resolveShopNavUrl,
} from "./merchant-nav-filter";
import {
  isMerchantNavCollapsible,
  type MerchantNavCollapsibleItem,
  type MerchantNavLinkItem,
  type MerchantNavItem,
  type MerchantNavSubItem,
} from "./data/merchant-sidebar-data";

const MARKETPLACE_NAV_URL = "/app/shops/$shopId/marketplace";

function withSubNavBadge(
  sub: MerchantNavSubItem,
  marketplaceBadge: string | undefined,
): MerchantNavSubItem {
  if (sub.url === MARKETPLACE_NAV_URL && marketplaceBadge) {
    return { ...sub, badge: marketplaceBadge };
  }
  return sub;
}

function withDynamicNavBadges(
  item: MerchantNavItem,
  marketplaceBadge: string | undefined,
): MerchantNavItem {
  if (isMerchantNavCollapsible(item)) {
    return {
      ...item,
      items: item.items.map((sub) => withSubNavBadge(sub, marketplaceBadge)),
    };
  }
  if (item.url === MARKETPLACE_NAV_URL && marketplaceBadge) {
    return { ...item, badge: marketplaceBadge };
  }
  return item;
}

function NavBadge({ children }: { children: ReactNode }) {
  return <Badge className="rounded-full px-1 py-0 text-xs">{children}</Badge>;
}

type ShopVerified = ReturnType<typeof useMerchantShops>["currentShop"] extends infer T
  ? T extends { verifiedLevel: infer V }
    ? V
    : null
  : null;

function getLinkDisabledAndTooltip(
  requiredPermission: string | undefined,
  currentPermissions: string[],
  verifiedLevel: ShopVerified,
  title: string,
): { disabled: boolean; tooltip: string } {
  const hasPermission =
    !requiredPermission ||
    hasMerchantPermission(currentPermissions, requiredPermission);
  const levelOk =
    !requiredPermission ||
    canUsePermissionWithLevel(
      requiredPermission as MerchantPermissionId,
      verifiedLevel,
    );
  const disabled = Boolean(hasPermission && !levelOk);
  const tooltip = disabled
    ? "ຕ້ອງຢືນຢັນລາຍການກ່ອນໃຊ້ຟີເຈີນີ້"
    : title;
  return { disabled, tooltip };
}

export function MerchantNavGroup() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const featureFlags = currentShop?.featureFlags;
  const { state, isMobile, setOpenMobile } = useSidebar();
  const href = useLocation({ select: (location) => location.href });
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const marketplaceBadge = useMarketplacePendingOrdersBadge(
    shopId,
    currentPermissions,
    featureFlags,
  );
  if (!shopId) return null;

  const navGroups = getVisibleMerchantNavGroups(
    currentPermissions,
    featureFlags,
  ).map((group) => ({
    ...group,
    items: group.items.map((item) =>
      withDynamicNavBadges(item, marketplaceBadge),
    ),
  }));

  return (
    <>
      {navGroups.map((group) => (
          <SidebarGroup key={group.title}>
            <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
            <SidebarMenu>
              {group.items.map((item) => {
                const key = isMerchantNavCollapsible(item)
                  ? `collapsible-${item.title}`
                  : `link-${item.url}`;

                if (!isMerchantNavCollapsible(item)) {
                  return (
                    <MerchantSidebarMenuLink
                      key={key}
                      item={item}
                      shopId={shopId}
                      href={href}
                      currentPermissions={currentPermissions}
                      verifiedLevel={verifiedLevel}
                      setOpenMobile={setOpenMobile}
                    />
                  );
                }

                if (state === "collapsed" && !isMobile) {
                  return (
                    <MerchantSidebarMenuCollapsedDropdown
                      key={key}
                      item={item}
                      shopId={shopId}
                      href={href}
                      currentPermissions={currentPermissions}
                      verifiedLevel={verifiedLevel}
                      setOpenMobile={setOpenMobile}
                    />
                  );
                }

                return (
                  <MerchantSidebarMenuCollapsible
                    key={key}
                    item={item}
                    shopId={shopId}
                    href={href}
                    currentPermissions={currentPermissions}
                    verifiedLevel={verifiedLevel}
                    setOpenMobile={setOpenMobile}
                  />
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
      ))}
    </>
  );
}

function MerchantSidebarMenuLink({
  item,
  shopId,
  href,
  currentPermissions,
  verifiedLevel,
  setOpenMobile,
}: {
  item: MerchantNavLinkItem;
  shopId: string;
  href: string;
  currentPermissions: string[];
  verifiedLevel: ShopVerified;
  setOpenMobile: (open: boolean) => void;
}) {
  const url = resolveShopNavUrl(item.url, shopId);
  const { disabled, tooltip } = getLinkDisabledAndTooltip(
    item.requiredMerchantPermission,
    currentPermissions,
    verifiedLevel,
    item.title,
  );
  const isActive = isMerchantNavLinkActive(href, item.url, shopId);

  return (
    <SidebarMenuItem>
      {disabled ? (
        <SidebarMenuButton
          tooltip={tooltip}
          disabled
          className="min-h-11 gap-3 rounded-xl px-3 py-2 text-sm font-medium opacity-60"
          isActive={isActive}
        >
          {item.icon && <item.icon />}
          <span>{item.title}</span>
          {item.badge && <NavBadge>{item.badge}</NavBadge>}
        </SidebarMenuButton>
      ) : (
        <SidebarMenuButton
          asChild
          tooltip={tooltip}
          isActive={isActive}
          className="min-h-11 gap-3 rounded-xl px-3 py-2 text-sm font-medium"
        >
          <Link to={url} onClick={() => setOpenMobile(false)}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
          </Link>
        </SidebarMenuButton>
      )}
    </SidebarMenuItem>
  );
}

function MerchantSidebarMenuCollapsible({
  item,
  shopId,
  href,
  currentPermissions,
  verifiedLevel,
  setOpenMobile,
}: {
  item: MerchantNavCollapsibleItem;
  shopId: string;
  href: string;
  currentPermissions: string[];
  verifiedLevel: ShopVerified;
  setOpenMobile: (open: boolean) => void;
}) {
  const defaultOpen = isMerchantCollapsibleActive(href, item, shopId);

  return (
    <Collapsible
      asChild
      defaultOpen={defaultOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            className="min-h-11 gap-3 rounded-xl px-3 py-2 text-sm font-medium"
          >
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
            <ChevronRight className="ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className="CollapsibleContent">
          <SidebarMenuSub>
            {item.items.map((subItem) => (
              <MerchantSidebarSubLink
                key={`${subItem.title}-${subItem.url}`}
                subItem={subItem}
                shopId={shopId}
                href={href}
                currentPermissions={currentPermissions}
                verifiedLevel={verifiedLevel}
                setOpenMobile={setOpenMobile}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function MerchantSidebarSubLink({
  subItem,
  shopId,
  href,
  currentPermissions,
  verifiedLevel,
  setOpenMobile,
}: {
  subItem: MerchantNavSubItem;
  shopId: string;
  href: string;
  currentPermissions: string[];
  verifiedLevel: ShopVerified;
  setOpenMobile: (open: boolean) => void;
}) {
  const url = resolveShopNavUrl(subItem.url, shopId);
  const { disabled, tooltip } = getLinkDisabledAndTooltip(
    subItem.requiredMerchantPermission,
    currentPermissions,
    verifiedLevel,
    subItem.title,
  );
  const isActive = isMerchantNavLinkActive(href, subItem.url, shopId);

  return (
    <SidebarMenuSubItem>
      {disabled ? (
        <SidebarMenuSubButton
          className="pointer-events-none opacity-60"
          isActive={isActive}
          aria-disabled
          title={tooltip}
        >
          {subItem.icon && <subItem.icon />}
          <span>{subItem.title}</span>
          {subItem.badge && <NavBadge>{subItem.badge}</NavBadge>}
        </SidebarMenuSubButton>
      ) : (
        <SidebarMenuSubButton asChild isActive={isActive}>
          <Link to={url} onClick={() => setOpenMobile(false)} title={tooltip}>
            {subItem.icon && <subItem.icon />}
            <span>{subItem.title}</span>
            {subItem.badge && <NavBadge>{subItem.badge}</NavBadge>}
          </Link>
        </SidebarMenuSubButton>
      )}
    </SidebarMenuSubItem>
  );
}

function MerchantSidebarMenuCollapsedDropdown({
  item,
  shopId,
  href,
  currentPermissions,
  verifiedLevel,
  setOpenMobile,
}: {
  item: MerchantNavCollapsibleItem;
  shopId: string;
  href: string;
  currentPermissions: string[];
  verifiedLevel: ShopVerified;
  setOpenMobile: (open: boolean) => void;
}) {
  const groupActive = isMerchantCollapsibleActive(href, item, shopId);

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            isActive={groupActive}
            className="min-h-11 gap-3 rounded-xl px-3 py-2 text-sm font-medium"
          >
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            {item.badge && <NavBadge>{item.badge}</NavBadge>}
            <ChevronRight className="ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" sideOffset={4}>
          <DropdownMenuLabel>
            {item.title} {item.badge ? `(${item.badge})` : ""}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {item.items.map((sub) => {
            const url = resolveShopNavUrl(sub.url, shopId);
            const { disabled } = getLinkDisabledAndTooltip(
              sub.requiredMerchantPermission,
              currentPermissions,
              verifiedLevel,
              sub.title,
            );
            const active = isMerchantNavLinkActive(href, sub.url, shopId);
            if (disabled) {
              return (
                <DropdownMenuItem
                  key={`${sub.title}-${sub.url}`}
                  disabled
                  className="opacity-60"
                >
                  {sub.icon && <sub.icon />}
                  <span className="max-w-52 text-wrap">{sub.title}</span>
                </DropdownMenuItem>
              );
            }
            return (
              <DropdownMenuItem key={`${sub.title}-${sub.url}`} asChild>
                <Link
                  to={url}
                  className={active ? "bg-secondary" : ""}
                  onClick={() => setOpenMobile(false)}
                >
                  {sub.icon && <sub.icon />}
                  <span className="max-w-52 text-wrap">{sub.title}</span>
                  {sub.badge && (
                    <span className="ms-auto text-xs">{sub.badge}</span>
                  )}
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
